import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { BaseDbObject } from "@/lib/models/db";
import { MimeType } from "@/lib/api/utils";
import { fetchApiGet, fetchApiPost, fetchApiPut, fetchApiDelete } from "@/lib/api/utils";

// Query schema for filtering
export const ExportSalesTrackerFilterSchema = z.object({
  page: z.number().optional().default(1),
  limit: z.number().optional().default(50),
  dateCreatedFrom: z.string().optional(), // YYYY-MM-DD
  dateCreatedTo: z.string().optional(), // YYYY-MM-DD
  groupedDateFrom: z.string().optional(), // YYYY-MM-DD
  groupedDateTo: z.string().optional(), // YYYY-MM-DD
  groupedId: z.string().optional(),
  customerId: z.string().uuid().optional(),
  commodity: z.string().optional(),
});

export type ExportSalesTrackerFilters = z.infer<typeof ExportSalesTrackerFilterSchema>;

// GET /export_sales_tracking

export interface ListSalesTrackerResponse {
    data: SalesExportTracker[];
    pagination: Pagination
}

export interface Pagination {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface SalesExportTracker extends BaseDbObject {
  id: string;
  customer_id: string | null;
  customer_client_id: string;
  commodity: string;
  grouped_id: string;
  grouped_date: string;
  amount: number | string; // Can be number or string depending on your Decimal handling

  Customer?: { client_id: string; long_name: string; };
  Contracts?: SalesExportTrackerContract[];
}

export interface SalesExportTrackerContract extends BaseDbObject {
  id: string;

  tracker_id: string;
  contract_number: string;
  has_been_signed: boolean;
}

export interface UploadReportResult {
  link: string;
  document_id: string;
  job_id: string;
}

export const UploadReportValidation = z.object({
  filename: z.string(),
  mime_type: z.enum([MimeType.Xlsx]),
})

// Build query params from filters
function buildQueryParams(filters: ExportSalesTrackerFilters): URLSearchParams {
  const queryParams = new URLSearchParams();
  
  if (filters.page) queryParams.append("page", filters.page.toString());
  if (filters.limit) queryParams.append("limit", filters.limit.toString());
  if (filters.dateCreatedFrom) queryParams.append("dateCreatedFrom", filters.dateCreatedFrom);
  if (filters.dateCreatedTo) queryParams.append("dateCreatedTo", filters.dateCreatedTo);
  if (filters.groupedDateFrom) queryParams.append("groupedDateFrom", filters.groupedDateFrom);
  if (filters.groupedDateTo) queryParams.append("groupedDateTo", filters.groupedDateTo);
  if (filters.groupedId) queryParams.append("groupedId", filters.groupedId);
  if (filters.customerId) queryParams.append("customerId", filters.customerId);
  if (filters.commodity) queryParams.append("commodity", filters.commodity);
  
  return queryParams;
}

// List export sales tracking with filters and pagination
export function useListExportSalesTracking(filters?: Partial<ExportSalesTrackerFilters>) {
  const validatedFilters = ExportSalesTrackerFilterSchema.parse(filters || {});
  const queryParams = buildQueryParams(validatedFilters);
  
  return useQuery({
    queryKey: ["exportSalesTracking", queryParams.toString()],
    queryFn: () => fetchApiGet<ListSalesTrackerResponse>("export_sales_tracking", queryParams),
    retry: 1,
  });
}

export function useCreateExportSalesTracker() {
  const queryClient = useQueryClient();
  
  return async (data: Partial<SalesExportTracker>) => {
    const result = await fetchApiPost<SalesExportTracker>("export_sales_tracking", data);
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["exportSalesTracking"] });
    }
    return result;
  };
}

export function useUpdateExportSalesTracker() {
  const queryClient = useQueryClient();
  
  return async (id: string, data: Partial<SalesExportTracker>) => {
    const result = await fetchApiPut<SalesExportTracker>(`export_sales_tracking/${id}`, data);
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["exportSalesTracking"] });
    }
    return result;
  };
}

export function useCreateExportSalesTrackerContract() {
  const queryClient = useQueryClient();
  
  return async (trackerId: string, data: Partial<SalesExportTrackerContract>) => {
    const result = await fetchApiPost<SalesExportTrackerContract>(
      `export_sales_tracking/contracts`,
      {
        tracker_id: trackerId,
        ...data,
      }
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["exportSalesTracking"] });
    }
    return result;
  };
}

export function useUpdateExportSalesTrackerContract() {
  const queryClient = useQueryClient();
  
  return async (trackerId: string, contractId: string, data: Partial<SalesExportTrackerContract>) => {
    const result = await fetchApiPut<SalesExportTrackerContract>(
      `export_sales_tracking/contracts/${contractId}`,
      data
    );
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["exportSalesTracking"] });
    }
    return result;
  };
}


export function useDeleteExportSalesTrackerContract() {
  const queryClient = useQueryClient();
  
  return async (trackerId: string, contractId: string) => {
    const result = await fetchApiDelete(`export_sales_tracking/contracts/${contractId}`);
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["exportSalesTracking"] });
    }
    return result;
  };
}

export function useDeleteExportSalesTracker() {
  const queryClient = useQueryClient();

  return async (id: string) => {
    const result = await fetchApiDelete(`export_sales_tracking/tracker/${id}`);
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: ["exportSalesTracking"] });
    }
  };
}



export function useGenerateIngestReportUpload() {
  const queryClient = useQueryClient();

  return async (data: z.infer<typeof UploadReportValidation>) => {
    const queryParams = new URLSearchParams();

    const result = await fetchApiPost<UploadReportResult>(
      `export_sales_tracking/ingest/upload`,
      data,
    );

    return result;
  };
}
